import { Grid, CardHeader, CardContent, Box, TextField, IconButton } from "@mui/material";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Form, FormikProvider, useFormik, FieldArray } from "formik";
import { IMAGE_BASE } from "../../utils/constants";
import LoadingButton from "@mui/lab/LoadingButton";
import CardLayout from "../../layouts/CardLayout";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import { AddCircle, RemoveCircle } from "@mui/icons-material";
import errorHandler from "../../utils/errorHandler";
import { BidPackageSchema } from "../../utils/Schema";
import { LeagueManagementService } from "../../services/AllServices";

function AddEditLeagueManagement() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const [data, setData] = useState();
  const ALLOWED_CHARACTERS_REGEX = /^[A-Za-z0-9\s]*$/;

  const { id } = useParams();
  const handleKeyPress = (e) => {
    if (!ALLOWED_CHARACTERS_REGEX.test(e.key)) {
      e.preventDefault();
    }
  };

  const fetchTaskData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await LeagueManagementService.getById({ id });
        const resData = response?.data?.data;
        setImagePreview(IMAGE_BASE + resData?.image);
        setData(resData);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchTaskData();
  }, [fetchTaskData]);

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: BidPackageSchema,
    initialValues: {
      name: data?.name || "",
      price: data?.price || "",
      earnBids: data?.earnBids || "",
      description: data?.description || [""],
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const payload = {
          id: id || undefined, // Include `id` if editing
          name: values.name.trim(),
          price: values.price,
          earnBids: values.earnBids,
          description: values.description,
          image: selectedImage || values.image,
        };

        const response = id
          ? await LeagueManagementService.edit(payload)
          : await LeagueManagementService.add(payload);
        toast.success(response?.data?.message, { toastId: "success" });
        
        // Navigate back to league management with preserved page and view mode
        const page = searchParams.get('page');
        const view = searchParams.get('view');
        
        if (page && view) {
          navigate(`/league-management?page=${page}&view=${view}`);
        } else if (page) {
          navigate(`/league-management?page=${page}`);
        } else if (view) {
          navigate(`/league-management?view=${view}`);
        } else {
          navigate(-1);
        }
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Box>
      <Helmet>
        <title>{id ? "Edit" : "Add"} Bid Package</title>
      </Helmet>
      <CardLayout>
        <CardHeader title={`${id ? "Edit" : "Add"} Bid Package`} />
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={6} md={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    required
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    name="name"
                    onPaste={(e) => {
                      const pastedText = e.clipboardData.getData("text");
                      if (/^\d/.test(pastedText)) {
                        e.preventDefault();
                      }
                    }}
                    onKeyPress={handleKeyPress}
                    error={Boolean(formik.touched.name && formik.errors.name)}
                    helperText={formik.touched.name && formik.errors.name}
                    inputProps={{ maxLength: 10 }} // Set max length to 50
                  />
                </Grid>
                <Grid item xs={6} md={6}>
                  <TextField
                    fullWidth
                    label="Price"
                    required
                    value={formik.values.price}
                    onChange={formik.handleChange}
                    name="price"
                    error={Boolean(formik.touched.price && formik.errors.price)}
                    helperText={formik.touched.price && formik.errors.price}
                    inputProps={{ maxLength: 7 }}
                  />
                </Grid>
                <Grid item xs={12} md={12}>
                  <TextField
                    fullWidth
                    label="Earn Bids"
                    required
                    value={formik.values.earnBids}
                    onChange={formik.handleChange}
                    name="earnBids"
                    error={Boolean(
                      formik.touched.earnBids && formik.errors.earnBids
                    )}
                    helperText={
                      formik.touched.earnBids && formik.errors.earnBids
                    }
                    inputProps={{ maxLength: 7 }} // Set max length to 7
                  />
                </Grid>
                <Grid item xs={12} md={12}>
                  <FieldArray
                    name="description"
                    render={(arrayHelpers) => (
                      <Box mt={3}>
                        {formik.values.description.map((point, index) => (
                          <Box
                            key={index}
                            display="flex"
                            alignItems="center"
                            mt={2}
                          >
                            <TextField
                              fullWidth
                              label={`Description Point ${index + 1}`}
                              name={`description[${index}]`}
                              value={point}
                              onChange={formik.handleChange}
                              error={Boolean(
                                formik.touched.description?.[index] &&
                                formik.errors.description?.[index]
                              )}
                              onKeyPress={handleKeyPress}
                              helperText={
                                formik.touched.description?.[index] &&
                                  formik.errors.description?.[index]
                                  ? formik.errors.description[index]
                                  : null
                              }
                            />
                            <IconButton
                              aria-label="Remove Description Point"
                              onClick={() => arrayHelpers.remove(index)}
                              color="error"
                              disabled={formik.values.description.length === 1}
                            >
                              <RemoveCircle />
                            </IconButton>
                            <IconButton
                              aria-label="Add Description Point"
                              onClick={() => arrayHelpers.push("")}
                              color="primary"
                            >
                              <AddCircle />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  />
                  <Box mt={3}>
                    <LoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={loading}
                    >
                      {id ? "Update" : "Add"} Bid Package
                    </LoadingButton>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default AddEditLeagueManagement;